<script>
    /*
    	MyServicesListItem.svelte

        NOTE: these are the full service records, not the lightweight indexes.

        Service record JSON as of 11/28/2022:

        acceptsInsurance
        additionalInformation
        addressLine1
        availability
        city
        costFreeText
        country
        county
        coverageAreas
        coverageCounties
        coverageGranularity
        coveragePostalCodes
        createdAt
        description
        eligibility
        formattedAddress
        geoId
        googleBusinessLink
        googleNumberRatings
        googleRating
        googleTestimonials
        id
        insuranceAccepted
        lastValidated
        lat
        locationPrecision
        lon
        mainEmail
        mainFacebook
        mainFax
        mainPhone
        mainTwitter
        mainWebsite
        minerNotes
        necessaryDocuments
        necessaryMaterials
        nextSteps
        operatingHours
        otherSocialMedia
        postalCode
        preferredPartner
        reviewed
        serviceName
        source
        state
        supportedInsurance
        supportedLanguages
        taxonomies
        updatedAt
        virtual

    */

    import { onMount } from 'svelte';

    import { formatPhoneNumber } from "../../common/utils.js";
    import { setWarningAlert, setInfoAlert } from '../../stores/AlertsStore';
    import Tooltip, { Wrapper } from '@smui/tooltip';

    // TCARE components
    import TCareRatingStars from '../tcare-ui/TCareRatingStars.svelte';
    import RemoveResourceButton from '../buttons/RemoveResourceButton.svelte';
    import MoreInfoButton from '../buttons/MoreInfoButton.svelte';

    
    const NOT_AVALABLE = "not available";
    const PREFERRED_BADGE_PATH = '/img/preferred_badge.png';
    const SVG_REMOVE_BUTTON  = '/img/garbage_can.svg';

    $: formattedService = formatServiceFields(service);
    $: borderStyle = getBorderStyle(formattedService);
    $: isShowingMoreInfo = false;

    export let service;
    export let onShowPopup;         // the delete API call happens on MyResourcesPage

    onMount(() => {
        // console.log('MyServicesListItem onMount');
        // console.log('formattedService:');
        // console.log(formattedService);
    })

    // must check for nulls
    const formatServiceFields = (service) => {
        let svc = {};

        svc.name = service['serviceName'] ? service['serviceName'] : '';
        svc.avgRating = service['googleRating'] ? service['googleRating'] : '';
        svc.numRatings = service['googleNumberRatings'] ? service['googleNumberRatings'] : 0;
        svc.address1 = service['addressLine1'] ? service['addressLine1'] : '';
        svc.cityStateZip = formatCityStateZip(service);

        // phone = service['main-phone'] ? service['main-phone'] : '';
        svc.phone = formatPhoneNumber(service['mainPhone']);               // utils.js
        svc.email = service['mainEmail'] ? service['mainEmail'] : '';
        svc.website = service['mainWebsite'] ? service['mainWebsite'] : '';

        svc.description = service['description'] ? service['description'] : '';
        if (svc.description.length < 3) svc.description = 'Description not available.';

        svc.isPreferred = service['preferredPartner'] ? service['preferredPartner'] > 0 : false;

        return svc;
    }

    const formatCityStateZip = (service) => {
        let city = service['city'] ? service['city'] : '';
        let state = service['state'] ? service['state'] : '';
        let zip = service['postalCode'] ? service['postalCode'] : '';
        if (city !== '') {
            return city + ', ' + state + ' ' + zip;
        } else {
            return state + ' ' + zip;
        }
    }

    const sanitizeWeblink = (link) =>  {
        let sanitized = { link: "", ok: false};
        if (!link || link.length < 3) return sanitized;

        try {
            let url = new URL(link);
            sanitized.link = url.toString();
            sanitized.ok = true;
            return sanitized;
        } catch (error) {
           // continue
        }

        // check for scheme
        let matches = link.match(/^([a-z0-9]+):\/\//);
        if (matches === null || matches.length === 0){
            sanitized.link = "http://" + link;
            sanitized.ok = true;
            return sanitized;
        }

        // TODO: more checks?

        sanitized.link = link;
        return sanitized
    }

    const getBorderStyle = (service) => {
        if (!service.isPreferred) return '';
        return 'border-left: 2px solid #FE4672; border-top-left-radius: 0px; border-bottom-left-radius: 0px';
    }
    

    const onMoreInfoButtonClick = () => {
        isShowingMoreInfo = !isShowingMoreInfo;
    }

    const launchWebSite = (link) => {
        let sanitized = sanitizeWeblink(link)
        if (!sanitized.ok) {
            setWarningAlert('This web link is not in a valid format', 2000);
            return;
        }
        window.open(sanitized.link, "_blank") || window.location.replace(sanitized.link);
    }

    const launchEmail = (link) => {
        let href = 'mailto:' + link;
        window.open(href, "_blank") || window.location.replace(href);
    }


</script>

<style>
    .service-list-item {
        position: relative;
        width: 95%;
        padding: 20px 2% 20px 3%;
        margin-bottom: 10px;
        font-size: 14px;
        color: var(--tcui-primary);
        background: #F8F3F8;
        border-radius: 25px;
    }


    .service-list-item .top {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        margin-bottom: 0px;
    }

    .service-list-item .middle {
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        margin-bottom: 0px;
    }

    .service-list-item .bottom {
        margin-top: 20px;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .service-list-item .left {
        width: 50%;
        height: 75px;
    }
    
    .service-list-item .right {
        width: 50%;
        height: 75px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
    }
    
    .service-list-item .h-10 {
        height: 10px;
    }

    .service-list-item .icon {
        width: 18px;
        height: 18px;
        margin-right: 10px;
    }


    /* FIELD FORMATTING */
    .service-list-item .name {
        font-size: 16px;
        font-weight: 800;
    }
    
    .service-list-item .row {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }
    
    .service-list-item .top-row {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    
    .service-list-item .bottom-row {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }
    
    .service-list-item .phone {
        width: 275px;
        user-select: none;
    }
    
    .service-list-item .email {
        width: 275px;
        cursor: pointer;
        text-decoration: underline;
    }

    .service-list-item .email:hover {
        color: var(--tcui-secondary);
    }

    .service-list-item .web {
        width: 275px;
        cursor: pointer;
        text-decoration: underline;
    }

    .service-list-item .web:hover {
        color: var(--tcui-secondary);
    }
    
    .service-list-item .address {
        height: 40px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: space-between;
    }

    .service-list-item .w-15 {
        width: 15px;
    }

    .service-list-item .more-info {
        width: 90%;
        padding: 3% 5%;
        user-select: none;
        background: #FFF4F7;
        border: 1px solid #FE6A8B;
        border-radius: 25px;    
    }

    .service-list-item .descr-title {
        margin-bottom: 15px;
        color: var(--tcui-secondary);
        font-size: 18px;
    }
    
    .service-list-item .descr-text {
        color: var(--tcui-primary);
        font-size: 12px;
    }
    


    /* top right corner */
    .preferred-badge {
        position: absolute;
        top: 10px;
        right: 12px;
    }

    @media screen and (max-width: 600px) {
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }

</style>

<!-- <div class="service-list-item" in:fade="{{ duration: 150 }}" out:fade="{{ duration: 150 }}"> -->
<div class="service-list-item" style={borderStyle} >
    {#if formattedService.isPreferred}
        <div class="preferred-badge">
            <img class="svg-connect" src={PREFERRED_BADGE_PATH} alt={'Preferred Partner'} draggable="false" >
        </div>
    {/if}

    <div class="top">
        <div class="top-row">
            <div class="name">{formattedService.name}</div>
        </div>
        <div class="row">
            <TCareRatingStars 
                source={formattedService} 
                prop={'avgRating'} 
                setValue={null}
                isReadOnly={true}
                starColor={'#611C73'}
            />
            {#if formattedService.numRatings !== '' && formattedService.numRatings !== 0}
                &nbsp; ({formattedService.numRatings})
            {/if}
        </div>
    </div>

    <div class="h-10" />

    <div class="middle">
        <div class="left">
            <div class="row">
                <div class="h-10" />
            </div>
            <div class="row">
                <div class="address">
                    <div>{formattedService.address1}</div>
                    <div>{formattedService.cityStateZip}</div>
                </div>
            </div>
        </div>

        <div class="right">
            <div class="row">
                <div class="icon"><img src='/img/phone-call.png' alt='Pin' draggable="false"/></div>
                {#if formattedService.phone.length > 2}
                    <div class="phone">{formattedService.phone}</div>
                {:else}
                    <div class="phone">Phone {NOT_AVALABLE}</div>
                {/if}
            </div>

            <div class="h-10" />

            <div class="row" on:click={() => launchEmail(formattedService.email)}>
                <div class="icon"><img src='/img/mail.png' alt='Pin' draggable="false"/></div>
                {#if formattedService.email.length > 2}
                    <div class="email ellipsis">{formattedService.email}</div>
                {:else}
                    <div class="phone">Email {NOT_AVALABLE}</div>
                {/if}
            </div>

            <div class="h-10" />

            <Wrapper>
                <div class="row" on:click={() => launchWebSite(formattedService.website)}>
                    <div class="icon"><img src='/img/web.png' alt='Pin' draggable="false"/></div>
                    {#if formattedService.website.length > 2}
                        <div class="web ellipsis">{formattedService.website}</div>
                        <Tooltip xPos="start" yPos="above">{formattedService.website}</Tooltip>
                    {:else}
                        <div class="phone">Website {NOT_AVALABLE}</div>
                    {/if}
                </div>
            </Wrapper>
            
        </div>
    </div>

    <div class="bottom">
        <div class="bottom-row">
            <MoreInfoButton onClick={onMoreInfoButtonClick} />
            <div class="w-15"></div>
            <RemoveResourceButton onClick={() => onShowPopup(service)} />
        </div>
    </div>

    {#if isShowingMoreInfo}
        <div class="h-20" />
        <div class="more-info">
            <div class="descr-title">Description</div>
            <div class="descr-text">{formattedService.description}</div>
        </div>
    {/if}

</div>
